import React, { useEffect, useId, useRef } from 'react'
import { useEscapeKey } from '../hooks/useEscapeKey.js'
import { useFocusTrap } from '../hooks/useFocusTrap.js'
import { useClickOutside } from '../hooks/useClickOutside.js'
import { IconButton } from '../IconButton/IconButton.jsx'

/**
 * Primary modal scaffold. Renders a backdrop, a containerised dialog with a
 * header (title + × close), and the body. Manages focus trap, Escape, click
 * outside, and body scroll lock.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - title: string (required for aria-labelledby)
 *   - width: 'sm' | 'md' | 'lg' (default 'md')
 *   - dismissOnBackdrop: boolean (default true)
 *   - dismissOnEscape: boolean (default true)
 *   - nested: boolean — when true, lifts to --z-modal-nested for confirm-on-modal stacks
 *   - children: dialog body
 */
export function Modal({
  open,
  onClose,
  title,
  width = 'md',
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  nested = false,
  initialFocusRef,
  children,
}) {
  const containerRef = useRef(null)
  const titleId = useId()

  useEscapeKey(() => {
    if (dismissOnEscape) onClose()
  }, open)

  useClickOutside(containerRef, () => {
    if (dismissOnBackdrop) onClose()
  }, open)

  useFocusTrap(containerRef, open)

  // Body scroll lock
  useEffect(() => {
    if (!open) return
    document.body.classList.add('modal-open')
    return () => document.body.classList.remove('modal-open')
  }, [open])

  // Optional initial focus override (e.g., the Cancel button on a danger
  // confirm, or the title input on a create form). Effect order matters:
  // this useEffect is declared AFTER useFocusTrap, so it runs after the
  // trap has focused its default first focusable, and the .focus() here
  // wins. Doing this synchronously (no rAF) keeps the override testable
  // under jsdom, which doesn't drive requestAnimationFrame on its own.
  useEffect(() => {
    if (!open || !initialFocusRef?.current) return
    initialFocusRef.current.focus()
  }, [open, initialFocusRef])

  if (!open) return null

  const overlayClasses = ['modal__overlay', nested && 'modal__overlay--nested']
    .filter(Boolean)
    .join(' ')

  return (
    <div className={overlayClasses}>
      <div
        ref={containerRef}
        className={`modal modal--${width}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="modal__header">
          <h2 id={titleId} className="modal__title">{title}</h2>
          <IconButton ariaLabel="Close" onClick={onClose}>×</IconButton>
        </header>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  )
}
